import { useEffect, useMemo } from 'react';
import { Box, Pagination, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectDayItemsForPage,
  selectDaysPage,
  selectDaysStatusByPage,
} from '../store/selectors/days-selectors';
import { fetchDays } from '../store/thunks/days-thunks';
import { AdSlot } from '../components/AdSlot';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import { DayCard } from '../components/DayCard';
import { EmptyResults } from '../components/EmptyResults';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `share.*` slot consumed by `<SeoHead>` below.
import { brandingFixture } from '../lib/branding/branding-fixture';

/**
 * Days-page surface (task 5.5, public-website spec).
 *
 * `<DaysPage>` at route `/days` renders the reverse-chronological
 * MUI `Card` list per Requirement 3.3, sourced from
 * `ListDaysWithSummariesV1` via the `fetchDays` thunk. Pagination
 * state lives in the URL (`?page=N`) so each page is shareable +
 * bookmarkable; the SPA uses `useSearchParams` to read it and
 * `Pagination`'s `onChange` to update it.
 *
 * Page size is server-pinned to 20 per Property 17 (and the
 * `fetchDays` thunk forwards it explicitly so the cache key remains
 * stable). The `count` for `<Pagination>` comes from
 * `Math.ceil(total / pageSize)` per Requirement 3.3.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

const PAGE_SIZE = 20;

/** Parses the `?page=N` search param; defaults to 1 on missing/invalid. */
function readPageFromSearch(search: URLSearchParams): number {
  const raw = search.get('page');
  if (!raw) return 1;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return parsed;
}

interface DaysHeaderProps {
  readonly page: number;
}

function DaysHeader({ page }: DaysHeaderProps): JSX.Element {
  return (
    <Typography variant="h1" component="h1">
      Days{page > 1 ? ` — page ${page}` : ''}
    </Typography>
  );
}

interface DaysListProps {
  readonly items: ReturnType<typeof selectDayItemsForPage>;
}

/**
 * Render the `<DayCard>` rows interleaved with sponsored placeholders
 * per the operator's "minimum one sponsored card per list, then one
 * per ten" requirement: a single ad after the first card when the
 * list has 1-9 entries, plus an additional ad after every 10th card
 * when the list grows beyond that.
 */
function DaysList({ items }: DaysListProps): JSX.Element {
  const tiles: Array<JSX.Element> = [];
  items.forEach((summary, index) => {
    tiles.push(<DayCard key={summary.date} summary={summary} />);
    if ((index + 1) % 10 === 0) {
      tiles.push(<AdSlot key={`ad-${index}`} slot={1} variant="default" />);
    }
  });
  // Guarantee one sponsored card even when the list is shorter than
  // 10 items — append a single inline banner at the tail when none
  // were injected by the per-10 rule above.
  if (items.length > 0 && items.length < 10) {
    tiles.push(<AdSlot key="ad-tail" slot={1} variant="default" />);
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      {tiles}
    </Box>
  );
}

interface DaysPaginationProps {
  readonly page: number;
  readonly total: number;
  readonly onChange: (next: number) => void;
}

function DaysPagination({ page, total, onChange }: DaysPaginationProps): JSX.Element | null {
  const count = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (count <= 1) return null;
  return (
    <Pagination
      page={page}
      count={count}
      onChange={(_e, next) => onChange(next)}
      sx={{ alignSelf: 'center', mt: 2 }}
    />
  );
}

/**
 * `<DaysPage>` — route `/days` per Requirement 3.3. Reads page index
 * from `?page=N`, dispatches `fetchDays` on mount + page change, and
 * renders the reverse-chronological `<DayCard>` list with
 * `<Pagination>` at the bottom. Surfaces `<EmptyResults>` on a
 * successful empty response and `<ApiUnavailableBanner>` on failure.
 */
export default function DaysPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = useMemo(() => readPageFromSearch(searchParams), [searchParams]);
  const dispatch = useAppDispatch();
  const payload = useAppSelector((s) => selectDaysPage(s, page));
  const items = useAppSelector((s) => selectDayItemsForPage(s, page));
  const status = useAppSelector((s) => selectDaysStatusByPage(s, page));
  useEffect(() => {
    void dispatch(fetchDays({ page, pageSize: PAGE_SIZE }));
  }, [dispatch, page]);
  const total = payload?.total ?? 0;
  const onChange = (next: number): void => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set('page', String(next));
      return updated;
    });
  };
  return renderDaysBody({ page, status, items, total, onChange });
}

interface DaysBodyArgs {
  readonly page: number;
  readonly status: 'idle' | 'loading' | 'succeeded' | 'failed';
  readonly items: ReturnType<typeof selectDayItemsForPage>;
  readonly total: number;
  readonly onChange: (next: number) => void;
}

function renderDaysBody(args: DaysBodyArgs): JSX.Element {
  const showBanner = args.status === 'failed';
  const showEmpty = args.status === 'succeeded' && args.items.length === 0;
  return (
    <Stack spacing={2}>
      <SeoHead
        route="/days"
        pageTitle="Daily archive"
        branding={brandingFixture.share}
        jsonLdType="WebSite"
      />
      <DaysHeader page={args.page} />
      {showBanner ? <ApiUnavailableBanner /> : null}
      {args.items.length > 0 ? <DaysList items={args.items} /> : null}
      {showEmpty ? <EmptyResults /> : null}
      <DaysPagination page={args.page} total={args.total} onChange={args.onChange} />
    </Stack>
  );
}
